import { useContext, useCallback, useRef } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client/react'
import { useUser } from '@fs/zion-user'

import { ImageOrganizeContext } from '../providers/ImageOrganizeProvider'
import queries from '../queries'
import mutations from '../mutations'
import { workflowStatuses } from '../helpers'
import useImageOrganizeModes from './useImageOrganizeModes'
import useGroupData from './useGroupData'
import fsOperationsCallHeader from '../../../utils/fsOperationsCallHeader'

const actionNames = {
  METADATA: 'AuditCoverages',
  DIVIDE: 'AuditBoundaries',
}

const useImageOrganizeActions = () => {
  const { cisId } = useUser()
  const { isDivideMode, isMetadataAllMode, isMetadataSingleMode } = useImageOrganizeModes()
  const { initialGroup: parentGroup, initialChildId } = useGroupData()
  const { dividersAltered, dividerOffsets, addNewDivider, removeDivider, skippedItemsMap, setSkippedItemsMap } =
    useContext(ImageOrganizeContext)
  const [updateWorkflowActionStatus, { loading }] = useMutation(mutations.updateWorkflowActionStatus())
  const [updateWorkflowActionStatusList] = useMutation(mutations.updateWorkflowActionStatusList())
  const [fetchStatusList] = useLazyQuery(queries.imageOrganizeStatusList())
  const alreadyValidated = useRef(false)

  const setSkippedStatus = useCallback(
    (itemId, skippedStatus) => {
      setSkippedItemsMap({ ...skippedItemsMap, [itemId]: skippedStatus })
    },
    [skippedItemsMap, setSkippedItemsMap]
  )

  const getSkippedStatus = useCallback((itemId) => Boolean(skippedItemsMap[itemId]), [skippedItemsMap])

  const updateMetadataWorkflowStatus = useCallback(
    async (rmsId, newStatus) => {
      if (!Object.values(workflowStatuses).includes(newStatus)) return false

      try {
        await updateWorkflowActionStatus({
          variables: {
            params: {
              action: actionNames.METADATA,
              newStatus,
              rmsId,
            },
          },
          context: { headers: { ...fsOperationsCallHeader } },
        })
      } catch (e) {
        return false
      }
      return true
    },
    [updateWorkflowActionStatus]
  )

  const validateWorkflowActions = useCallback(
    async (editingNotAvailableOverlay) => {
      if (alreadyValidated.current || !cisId) return

      let groupIds
      if ((isDivideMode || isMetadataSingleMode) && initialChildId) groupIds = [initialChildId]
      else if (isMetadataAllMode && Array.isArray(parentGroup?.childGroups))
        groupIds = parentGroup.childGroups.map((el) => el.id)

      if (groupIds) {
        alreadyValidated.current = true
        const result = await fetchStatusList({ variables: { rmsIdList: groupIds } })
        const statusList = result.data?.imageOrganizeStatusList ?? []

        let isInProgressByAnotherUser = false
        statusList.forEach((item) => {
          let statusItem = item.correctMetadata
          if (isDivideMode) statusItem = item.divide

          if (statusItem?.status === workflowStatuses.IN_PROGRESS && statusItem?.userId !== cisId)
            isInProgressByAnotherUser = true
        })

        if (isInProgressByAnotherUser && !isMetadataAllMode) {
          if (editingNotAvailableOverlay) editingNotAvailableOverlay.handleClick()
          return
        }

        if (isMetadataAllMode) {
          // If correcting metadata for all items, mark any available items as IN PROGRESS
          const availableIds = statusList
            .filter((item) => item.correctMetadata?.status === workflowStatuses.AVAILABLE)
            .map((item) => item.id)

          if (availableIds.length) {
            const input = availableIds.map((id) => ({
              action: actionNames.METADATA,
              rmsId: id,
              newStatus: workflowStatuses.IN_PROGRESS,
            }))
            await updateWorkflowActionStatusList({
              variables: { actionStatusList: input },
              context: { headers: { ...fsOperationsCallHeader } },
            })
          }
        } else if (isDivideMode || isMetadataSingleMode) {
          // Mark the current item as IN PROGRESS if it is currently AVAILABLE
          const statusItem = isDivideMode ? statusList?.[0]?.divide : statusList?.[0]?.correctMetadata
          if (statusItem?.status === workflowStatuses.AVAILABLE) {
            await updateWorkflowActionStatus({
              variables: {
                params: {
                  action: isDivideMode ? actionNames.DIVIDE : actionNames.METADATA,
                  rmsId: initialChildId,
                  newStatus: workflowStatuses.IN_PROGRESS,
                },
              },
              context: { headers: { ...fsOperationsCallHeader } },
            })
          }
        }
      }
    },
    [
      cisId,
      isDivideMode,
      isMetadataSingleMode,
      initialChildId,
      isMetadataAllMode,
      parentGroup,
      fetchStatusList,
      updateWorkflowActionStatusList,
      updateWorkflowActionStatus,
    ]
  )

  return {
    addNewDivider,
    dividerOffsets,
    dividersAltered,
    removeDivider,
    updateMetadataWorkflowStatus,
    loading,
    validateWorkflowActions,
    setSkippedStatus,
    getSkippedStatus,
  }
}

export default useImageOrganizeActions
